import { Injectable, OnDestroy } from "@angular/core";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { filter, takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class NavigationHistoryService implements OnDestroy {
    private history: { url: string; queryParams: { [key: string]: any } }[] = [];
    private readonly MAX_HISTORY_LENGTH = 15;
    private destroyed$ = new Subject<void>();

    constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

    initialize(): void {
        this.history = [];

        this.addToHistory(this.router.url, this.getQueryParams());

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroyed$))
            .subscribe((event: NavigationEnd) => {
                const queryParams = this.getQueryParams();
                this.addToHistory(decodeURI(event.urlAfterRedirects), queryParams);
            });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private addToHistory(url: string, queryParams: { [key: string]: any }): void {
        const urlWithoutParams = url.split("?")[0];
        if (!url || (this.history.length > 0 && this.history[this.history.length - 1].url === urlWithoutParams)) {
            return;
        }

        this.history.push({ url: urlWithoutParams, queryParams });

        if (this.history.length > this.MAX_HISTORY_LENGTH) {
            this.history.shift();
        }
    }

    getPreviousUrl(): { url: string; queryParams: { [key: string]: any } } | null {
        if (this.history.length > 1) {
            return this.history[this.history.length - 2];
        }
        return null;
    }

    removeCurrentUrl(): void {
        if (this.history.length > 0) {
            this.history.pop();
        }
    }

    clearHistory(): void {
        this.history = [];
    }

    removePreviousUrl(): void {
        if (this.history.length > 1) {
            this.history.splice(this.history.length - 2, 1);
        }
    }

    private async getQueryParams(): Promise<{ [key: string]: any }> {
        return firstValueFrom(this.activatedRoute.queryParams);
    }
}
